import { FC } from "react";
import ButtonBase from "@mui/material/ButtonBase";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";
import { useCurrentAppVersions } from "../../hooks/useAppVersions";
import { APP_VERSION_PLATFORMS } from "../../domain/appVersion.interface";

interface AppVersionsFooterProps {
	/** Opens the Versions dialog with the detail. */
	onClick: () => void;
}

/**
 * SIMS-9441 — the current version of each component, at the foot of the
 * side menu. Hidden until at least one version is registered.
 */
const AppVersionsFooter: FC<AppVersionsFooterProps> = ({ onClick }) => {
	const { data: current = [] } = useCurrentAppVersions();

	const parts = APP_VERSION_PLATFORMS.flatMap(({ value, label }) => {
		const version = current.find((v) => v.platform === value);
		return version ? [`${label} ${version.version}`] : [];
	});

	if (parts.length === 0) {
		return null;
	}

	return (
		<ButtonBase
			onClick={onClick}
			aria-label="Show versions"
			data-testid="app-versions-footer"
			sx={{
				mt: "auto",
				mx: 1,
				px: 1,
				py: 0.75,
				borderRadius: "0.5rem",
				flexDirection: "column",
				alignItems: "flex-start",
				"&:hover": {
					backgroundColor: (theme) => alpha(theme.palette.surface.main, 0.08),
				},
			}}
		>
			{parts.map((part) => (
				<Typography
					key={part}
					sx={{
						color: (theme) => theme.palette.surface.main,
						fontSize: "0.6875rem",
						lineHeight: 1.5,
						opacity: 0.8,
					}}
				>
					{part}
				</Typography>
			))}
		</ButtonBase>
	);
};

export default AppVersionsFooter;
